import React from "react";
import {
  Boxes,
  Globe2,
  Package,
  Ship,
  CalendarDays,
  CreditCard,
  BadgeCheck,
  ShieldCheck,
  FileText,
  Paperclip,
  ChevronDown,
  Save,
  ArrowRight,
  CircleDot,
  Plug,
  Layers,
  Target,
  Hash,
} from "lucide-react";

const FIELDS = [
  {
    icon: Boxes,
    label: "Product",
    value: "Cotton Hand Towels — 600 GSM",
    meta: "SKU-CT-600",
  },
  {
    icon: Layers,
    label: "Commodity",
    value: "Textiles › Home Textiles",
    meta: "HS 6302.60",
  },
  {
    icon: Hash,
    label: "Quantity",
    value: "24,000 pcs",
    meta: "≈ 2 × 40' HQ",
  },
  {
    icon: Globe2,
    label: "Destination Country",
    value: "Hamburg, Germany",
    meta: "DE",
  },
  {
    icon: Package,
    label: "Packaging",
    value: "Polybag + Carton",
    meta: "12 / carton",
  },
  {
    icon: Ship,
    label: "Incoterm",
    value: "FOB — Izmir Port",
    meta: "Incoterms 2020",
  },
  {
    icon: CalendarDays,
    label: "Target Delivery",
    value: "Mar 18, 2026",
    meta: "13 weeks",
  },
  {
    icon: CreditCard,
    label: "Payment Terms",
    value: "LC at Sight",
    meta: "30% advance",
  },
];

const CHIPS = [
  { icon: BadgeCheck, label: "OEKO-TEX 100", tone: "emerald" },
  { icon: BadgeCheck, label: "GOTS", tone: "emerald" },
  { icon: ShieldCheck, label: "Pre-shipment Inspection", tone: "blue" },
  { icon: FileText, label: "Test Report Required", tone: "neutral" },
];

const DOCS = [
  { name: "Tech_Pack_v3.pdf", size: "1.2 MB" },
  { name: "Color_Reference.png", size: "428 KB" },
  { name: "Brand_Label_Spec.pdf", size: "640 KB" },
];

const toneStyles = {
  emerald: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  blue: "bg-[#0044FF]/8 text-[#0044FF] border-[#0044FF]/20",
  neutral: "bg-black/[0.04] text-black/70 border-black/[0.08]",
};

export default function RfqWorkspaceMock() {
  return (
    <div
      data-testid="hero-workspace-mock"
      className="relative mx-auto rounded-[28px] border border-black/[0.06] bg-white/95 backdrop-blur shadow-elevated overflow-hidden"
    >
      {/* OS top bar */}
      <div className="flex items-center justify-between border-b border-black/[0.06] bg-gradient-to-b from-white to-[#FAFAFA] px-4 py-2.5">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
          </div>
          <span className="ml-3 font-mono text-[10.5px] tracking-[0.18em] text-black/45">
            demaxtore.workspace / rfq / new
          </span>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 rounded-md border border-black/[0.06] bg-white px-2 py-1">
          <CircleDot className="h-3 w-3 text-emerald-500" />
          <span className="font-mono text-[10px] tracking-[0.18em] text-black/55">
            AUTOSAVED · 12:42
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px]">
        {/* Left panel */}
        <div className="border-b lg:border-b-0 lg:border-r border-black/[0.06] p-5 sm:p-7 bg-[#FAFAFB]/60">
          {/* Title row */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10.5px] tracking-[0.2em] text-black/45">
                  RFQ-0241
                </span>
                <span className="rounded-md border border-black/[0.08] bg-white px-1.5 py-0.5 font-mono text-[10px] tracking-[0.16em] text-black/55">
                  DRAFT
                </span>
              </div>
              <h3 className="mt-2 font-display text-[22px] sm:text-[26px] font-bold tracking-tight">
                Cotton Hand Towels — Bulk Sourcing
              </h3>
              <p className="mt-1 text-[13px] text-black/55">
                One structured request · 12 verified manufacturers will receive it
              </p>
            </div>
            <div className="hidden sm:flex flex-col items-end">
              <span className="font-mono text-[10px] tracking-[0.2em] text-black/45">
                COMPLETION
              </span>
              <span className="font-display text-[26px] font-bold leading-none mt-1">
                92<span className="text-[16px] text-black/40 font-semibold">%</span>
              </span>
              <div className="mt-1.5 h-1.5 w-32 rounded-full bg-black/[0.06] overflow-hidden">
                <div className="h-full w-[92%] rounded-full bg-gradient-to-r from-[#0044FF] to-emerald-500" />
              </div>
            </div>
          </div>

          {/* Fields grid */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {FIELDS.map(({ icon: Icon, label, value, meta }) => (
              <div
                key={label}
                className="group rounded-xl border border-black/[0.06] bg-white p-3.5 hover:border-black/[0.14] hover:shadow-soft transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-black/[0.04] text-black/70 group-hover:bg-[#0044FF]/10 group-hover:text-[#0044FF] transition-colors">
                      <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.18em] text-black/45">
                      {label.toUpperCase()}
                    </span>
                  </div>
                  <span className="font-mono text-[10px] text-black/35">{meta}</span>
                </div>
                <div className="mt-2 font-display text-[14.5px] font-semibold tracking-tight text-[#0A0A0A]">
                  {value}
                </div>
              </div>
            ))}
          </div>

          {/* Certificates / Inspection / Special */}
          <div className="mt-5 rounded-xl border border-black/[0.06] bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ChevronDown className="h-3.5 w-3.5 text-black/45" />
                <span className="font-mono text-[10.5px] tracking-[0.2em] text-black/55">
                  QUALITY · CERTIFICATES · INSPECTION
                </span>
              </div>
              <span className="font-mono text-[10px] tracking-[0.18em] text-emerald-600">
                COMPLETE
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {CHIPS.map(({ icon: Icon, label, tone }) => (
                <span
                  key={label}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-medium ${toneStyles[tone]}`}
                >
                  <Icon className="h-3 w-3" strokeWidth={2} />
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Documents */}
          <div className="mt-3 rounded-xl border border-black/[0.06] bg-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ChevronDown className="h-3.5 w-3.5 text-black/45" />
                <span className="font-mono text-[10.5px] tracking-[0.2em] text-black/55">
                  SUPPORTING DOCUMENTS
                </span>
              </div>
              <span className="font-mono text-[10px] tracking-[0.18em] text-black/45">
                3 FILES
              </span>
            </div>
            <ul className="mt-3 grid gap-1.5">
              {DOCS.map((d) => (
                <li
                  key={d.name}
                  className="flex items-center justify-between rounded-lg bg-[#F5F6F8] px-3 py-2 text-[12.5px]"
                >
                  <span className="flex items-center gap-2 text-black/80">
                    <Paperclip className="h-3.5 w-3.5 text-black/45" />
                    {d.name}
                  </span>
                  <span className="font-mono text-[10px] text-black/40">{d.size}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right panel */}
        <aside className="p-5 sm:p-6 bg-white">
          <div className="rounded-xl border border-black/[0.06] bg-[#FAFAFB] p-4">
            <span className="font-mono text-[10px] tracking-[0.2em] text-black/45">
              RFQ STATUS
            </span>
            <div className="mt-2 flex items-center gap-2">
              <span className="inline-flex h-2 w-2 rounded-full bg-amber-500 pulse-dot" />
              <span className="font-display text-[18px] font-semibold">Draft</span>
            </div>
            <p className="mt-1 text-[12px] text-black/55">
              Ready for submission once verified.
            </p>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-black/[0.06] p-3.5">
              <span className="font-mono text-[10px] tracking-[0.2em] text-black/45">
                EST. MANUFACTURERS
              </span>
              <div className="mt-1.5 flex items-baseline gap-1">
                <span className="font-display text-[28px] font-black leading-none">12</span>
                <span className="text-[11px] text-emerald-600 font-medium">verified</span>
              </div>
            </div>
            <div className="rounded-xl border border-black/[0.06] p-3.5">
              <span className="font-mono text-[10px] tracking-[0.2em] text-black/45">
                WORKSPACE
              </span>
              <div className="mt-1.5 flex items-center gap-1.5">
                <Plug className="h-3.5 w-3.5 text-emerald-600" />
                <span className="font-display text-[14px] font-semibold">Connected</span>
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-black/[0.06] p-3.5">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-[0.2em] text-black/45">
                COMPLETION
              </span>
              <span className="font-mono text-[11px] text-[#0044FF] font-semibold">92%</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-black/[0.06] overflow-hidden">
              <div className="h-full w-[92%] rounded-full bg-gradient-to-r from-[#0044FF] to-emerald-500" />
            </div>
            <ul className="mt-3 space-y-1.5 text-[12px]">
              <li className="flex items-center justify-between text-black/65">
                <span>Product info</span>
                <span className="text-emerald-600 font-medium">✓</span>
              </li>
              <li className="flex items-center justify-between text-black/65">
                <span>Commercial terms</span>
                <span className="text-emerald-600 font-medium">✓</span>
              </li>
              <li className="flex items-center justify-between text-black/65">
                <span>Documents</span>
                <span className="text-emerald-600 font-medium">✓</span>
              </li>
              <li className="flex items-center justify-between text-black/55">
                <span>Buyer notes</span>
                <span className="text-amber-600 font-medium">2 left</span>
              </li>
            </ul>
          </div>

          <div className="mt-4 grid gap-2">
            <button className="btn-secondary text-[12.5px] inline-flex items-center justify-center gap-1.5">
              <Save className="h-3.5 w-3.5" /> Save Draft
            </button>
            <button className="btn-primary text-[12.5px] inline-flex items-center justify-center gap-1.5">
              Submit RFQ
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <button className="text-[12px] inline-flex items-center justify-center gap-1.5 text-[#0044FF] hover:underline mt-1">
              <Target className="h-3.5 w-3.5" />
              Continue with CommodityBid™
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
